import { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import { useAuth } from "../hooks/useAuth"
import {FileText, Calendar, ChevronLeft, ChevronRight, Clock, Users, User, CheckCircle, XCircle, AlertCircle, Filter, Paperclip, Download, Link2} from 'lucide-react'
import { LinkedIssueBadges } from "./LinkedIssueBadges"
import { setPendingIssueTarget, type LinkedIssue } from "../lib/issueLink"

const ACTIVITY_COLORS = {
  "개발/설계": "#3B82F6",
  "영업/견적/PM/기획": "#10B981",
  "제작/조립/구매": "#F59E0B",
  "시험/검증/테스트": "#8B5CF6",
  "설치/납품/AS": "#EF4444",
  "지원/문서/교육/미팅": "#EC4899"
}

export function WeeklyReportReview() {
  const { user, userRole } = useAuth()
  const [selectedWeek, setSelectedWeek] = useState<Date>(getMonday(new Date()))
  const [reports, setReports] = useState<any[]>([])
  const [rejectedReports, setRejectedReports] = useState<any[]>([])
  const [selectedReport, setSelectedReport] = useState<any>(null)
  const [prevWeekReport, setPrevWeekReport] = useState<any>(null)
  const [timeEntries, setTimeEntries] = useState<any[]>([])
  const [attachments, setAttachments] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [showRejected, setShowRejected] = useState(false)
  const [currentTeamId, setCurrentTeamId] = useState<string>("")
  const [teams, setTeams] = useState<any[]>([])
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<string>("all")
  
  // 반려 모달 관련 상태
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState("")
  const [reportToReject, setReportToReject] = useState<any>(null)
  
  // CEO 반려 모달 관련 상태
  const [showCeoRejectModal, setShowCeoRejectModal] = useState(false)
  const [ceoRejectReason, setCeoRejectReason] = useState("")
  const [reportToCeoReject, setReportToCeoReject] = useState<any>(null)
  
  const isManager = userRole === "MANAGER"
  const isCLevel = userRole === "C_LEVEL"

  // 프로필 조회하여 teamId 가져오기
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user?.userId || user.userRole === "ADMIN") return
      
      try {
        const profileResult = await lumi.entities.userProfiles3.list({
          filter: { userId: user.userId },
          limit: 1
        })
        
        if (profileResult.list && profileResult.list.length > 0) {
          const profile = profileResult.list[0]
          setCurrentTeamId(profile.teamId || "")
          console.log("[WeeklyReportReview] 프로필 조회 완료:", {
            userId: user.userId,
            teamId: profile.teamId,
            teamName: profile.teamName,
            role: profile.role
          })
        }
      } catch (error) {
        console.error("[WeeklyReportReview] 프로필 조회 실패:", error)
      }
    }
    
    loadUserProfile()
  }, [user])

  // C-Level용: 팀 목록 로드
  useEffect(() => {
    if (isCLevel) {
      loadTeams()
    }
  }, [isCLevel])

  const loadTeams = async () => {
    try {
      const result = await lumi.entities.teams.list({
        filter: { enabled: true }
      })
      console.log("[WeeklyReportReview] 팀 목록 로드:", result.list.length)
      setTeams(result.list)
    } catch (error) {
      console.error("[WeeklyReportReview] 팀 목록 로드 실패:", error)
    }
  }

  useEffect(() => {
    // Manager는 teamId 필수, CLevel은 바로 실행
    if (isManager && !currentTeamId) {
      console.log("[WeeklyReportReview] Skipping: Manager without teamId")
      return
    }
    
    // Manager: currentTeamId 필수
    // CLevel: 바로 실행 가능
    if ((isManager && currentTeamId) || userRole === "C_LEVEL") {
      loadWeeklyReports()
    }
  }, [selectedWeek, currentTeamId, userRole, selectedTeamFilter, isManager])

  useEffect(() => {
    if (selectedReport) {
      loadTimeEntriesForReport(selectedReport)
      loadAttachmentsForReport(selectedReport)
      loadPreviousWeekReport(selectedReport)
    } else {
      setPrevWeekReport(null)
    }
  }, [selectedReport])

  const loadWeeklyReports = async () => {
    // Manager인데 teamId가 아직 로드되지 않았으면 대기
    if (isManager && !currentTeamId) {
      console.log("[WeeklyReportReview] Manager teamId not loaded yet, skipping...")
      return
    }
    
    setLoading(true)
    try {
      const weekStartDate = new Date(selectedWeek)
      weekStartDate.setHours(0, 0, 0, 0)
      const weekStart = weekStartDate.toISOString()
      
      const weekEndDate = getWeekEnd(selectedWeek)
      weekEndDate.setHours(0, 0, 0, 0)
      const weekEnd = weekEndDate.toISOString()

      console.log("[WeeklyReportReview] Loading reports with:", {
        weekStart,
        weekEnd,
        userRole,
        teamId: currentTeamId,
        isManager
      })

      // Manager인 경우 DB 조회 시부터 teamId 필터 포함
      const filter: any = {
        weekStart,
        weekEnd,
        status: "submitted"
      }
      
      if (isManager && currentTeamId) {
        filter.teamId = currentTeamId
      }

      const result = await lumi.entities.weeklyReports.list({
        filter
      })

      console.log("[WeeklyReportReview] Total reports fetched:", result.list.length)
      console.log("[WeeklyReportReview] All reports:", result.list.map((r: any) => ({
        userName: r.userName,
        teamId: r.teamId,
        teamName: r.teamName
      })))

      // 임원이 작성한 보고서 필터링: authorId의 role 확인
      const authorIds = [...new Set(result.list.map((r: any) => r.userId))]
      const authorProfiles = await Promise.all(
        authorIds.map(async (userId) => {
          try {
            const profileResult = await lumi.entities.userProfiles3.list({
              filter: { userId },
              limit: 1
            })
            return profileResult.list.length > 0 ? profileResult.list[0] : null
          } catch (error) {
            console.error(`[WeeklyReportReview] Failed to load profile for ${userId}:`, error)
            return null
          }
        })
      )

      // C-Level 필터링: "임원" 팀 소속 C-Level은 표시, 다른 팀의 C-Level은 숨김
      const cLevelUserIds = new Set(
        authorProfiles
          .filter((profile) => profile && profile.role === "C_LEVEL")
          .map((profile) => profile!.userId)
      )

      console.log("[WeeklyReportReview] C-Level authors:", Array.from(cLevelUserIds))

      let filteredReports = result.list.filter((report: any) => {
        const isAuthorCLevel = cLevelUserIds.has(report.userId)
        
        // C-Level이 아니면 표시
        if (!isAuthorCLevel) return true
        
        // C-Level이면서 "임원" 팀 소속이면 표시
        if (report.teamName === "임원") {
          console.log(`[WeeklyReportReview] Showing C-Level report by ${report.userName} (임원 팀)`)
          return true
        }
        
        // 그 외 C-Level은 숨김
        console.log(`[WeeklyReportReview] Filtering out C-Level report by ${report.userName}`)
        return false
      })
      
      // C-Level: 선택한 팀으로 필터링
      if (isCLevel && selectedTeamFilter !== "all") {
        console.log("[WeeklyReportReview] C-Level - Filtering by teamId:", selectedTeamFilter)
        filteredReports = filteredReports.filter((report: any) => {
          const match = report.teamId === selectedTeamFilter
          console.log(`[WeeklyReportReview] Report ${report.userName} (${report.teamId}) - Match: ${match}`)
          return match
        })
        console.log("[WeeklyReportReview] C-Level filtered reports count:", filteredReports.length)
      }
      
      // Manager 필터링은 DB 쿼리(130-132번 라인)에서 이미 적용되었으므로 중복 필터링 제거
      console.log("[WeeklyReportReview] Manager filtering already applied in DB query")
      
      // 승인/대기 보고서와 반려 보고서 분리
      // CEO 반려된 보고서는 주간보고 확인 화면에서 숨김
      const approved = filteredReports.filter((report: any) => 
        report.managerApproval !== "rejected" && report.ceoApproval !== "rejected"
      )
      const rejected = filteredReports.filter((report: any) => 
        report.managerApproval === "rejected" || report.ceoApproval === "rejected"
      )

      // 제출시간 기준으로 정렬
      const sortedReports = approved.sort((a: any, b: any) => {
        return new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
      })
      const sortedRejected = rejected.sort((a: any, b: any) => {
        return new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
      })

      setReports(sortedReports)
      setRejectedReports(sortedRejected)
      setSelectedReport(null)
      setTimeEntries([])
    } catch (error) {
      console.error("Failed to load weekly reports:", error)
    } finally {
      setLoading(false)
    }
  }

  const loadTimeEntriesForReport = async (report: any) => {
    try {
      const result = await lumi.entities.time_entries2.list({
        filter: {
          userId: report.userId,
          weekStart: report.weekStart,
          status: "submitted"
        }
      })

      setTimeEntries(result.list)
    } catch (error) {
      console.error("Failed to load time entries:", error)
      setTimeEntries([])
    }
  }

  const loadAttachmentsForReport = async (report: any) => {
    try {
      const result = await lumi.entities.attachments.list({
        filter: {
          reportId: report._id
        }
      })

      setAttachments(result.list)
    } catch (error) {
      console.error("Failed to load attachments:", error)
      setAttachments([])
    }
  }

  const loadPreviousWeekReport = async (report: any) => {
    try {
      const prevStart = new Date(report.weekStart)
      prevStart.setDate(prevStart.getDate() - 7)
      prevStart.setHours(0, 0, 0, 0)

      const result = await lumi.entities.weeklyReports.list({
        filter: {
          userId: report.userId,
          weekStart: prevStart.toISOString()
        }
      })

      setPrevWeekReport(result.list.length > 0 ? result.list[0] : null)
    } catch (error) {
      console.error("Failed to load previous week report:", error)
      setPrevWeekReport(null)
    }
  }

  const previousWeek = () => {
    const newDate = new Date(selectedWeek)
    newDate.setDate(newDate.getDate() - 7)
    setSelectedWeek(newDate)
  }

  const nextWeek = () => {
    const newDate = new Date(selectedWeek)
    newDate.setDate(newDate.getDate() + 7)
    setSelectedWeek(newDate)
  }

  const handleApprove = async (report: any) => {
    if (!isManager) {
      alert("팀장만 승인/반려가 가능합니다.")
      return
    }
    
    if (!window.confirm(`${report.userName}님의 주간보고를 승인하시겠습니까?`)) {
      return
    }
    
    try {
      const now = new Date().toISOString()
      
      await lumi.entities.weeklyReports.update(report._id, {
        managerApproval: "approved",
        isApproved: true,
        approvedBy: user?.userId,
        approvalManagerId: user?.userId,
        approvedAt: now,
        ceoApproval: "pending"
      })
      
      // 승인 시 반려 사유 데이터 삭제
      try {
        const rejectionResult = await lumi.entities.rejection_reasons.list({
          filter: {
            reportId: report._id,
            weekStart: report.weekStart,
            weekEnd: report.weekEnd
          }
        })
        
        if (rejectionResult.list.length > 0) {
          for (const rejection of rejectionResult.list) {
            await lumi.entities.rejection_reasons.delete(rejection._id)
            console.log("[APPROVAL] Deleted rejection reason:", rejection._id)
          }
        }
      } catch (deleteError) {
        console.error("[APPROVAL] Failed to delete rejection reasons:", deleteError)
      }

      // User에게 승인 알림 생성
      const weekStartDate = new Date(report.weekStart)
      const month = weekStartDate.getMonth() + 1
      const firstDay = new Date(weekStartDate.getFullYear(), weekStartDate.getMonth(), 1)
      const weekNumber = Math.ceil((weekStartDate.getDate() + firstDay.getDay()) / 7)

      const notificationData = {
        userId: report.userId,
        type: "approved",
        message: `${month}월 ${weekNumber}주차 주간보고가 승인되었습니다.`,
        reportId: report._id,
        authorId: report.userId,
        authorName: report.userName,
        teamId: report.teamId,
        teamName: report.teamName,
        weekStart: report.weekStart,
        weekEnd: report.weekEnd,
        createdAt: now,
        isRead: false,
        isDeleted: false
      }
      
      console.log("[APPROVAL] Creating notification with data:", JSON.stringify(notificationData, null, 2))
      
      const notification = await lumi.entities.notifications.create(notificationData)
      
      console.log("[APPROVAL] Notification created successfully:", notification._id)
      console.log("[APPROVAL] Full notification object:", JSON.stringify(notification, null, 2))
      
      // 생성된 알림이 실제로 DB에 있는지 확인
      const verifyResult = await lumi.entities.notifications.list({
        filter: { _id: notification._id },
        limit: 1
      })
      console.log("[APPROVAL] Verification - notification in DB:", verifyResult.list.length > 0 ? "YES" : "NO")
      if (verifyResult.list.length > 0) {
        console.log("[APPROVAL] Verified notification:", JSON.stringify(verifyResult.list[0], null, 2))
      }
      
      alert("✅ 승인이 완료되었습니다.")
      await loadWeeklyReports()
      
      // 선택된 보고서 갱신
      if (selectedReport?._id === report._id) {
        const updated = { 
          ...report, 
          managerApproval: "approved",
          isApproved: true,
          approvedBy: user?.userId,
          approvedAt: new Date().toISOString()
        }
        setSelectedReport(updated)
      }
    } catch (error) {
      console.error("Failed to approve report:", error)
      alert("❌ 승인 처리 중 오류가 발생했습니다.")
    }
  }
  
  const openRejectModal = (report: any) => {
    setReportToReject(report)
    setRejectReason("")
    setShowRejectModal(true)
  }
  
  const closeRejectModal = () => {
    setShowRejectModal(false)
    setReportToReject(null)
    setRejectReason("")
  }
  
  const handleReject = async () => {
    if (!reportToReject) return
    
    if (!rejectReason.trim() || rejectReason.trim().length < 10) {
      alert("반려 사유를 10자 이상 입력해주세요.")
      return
    }
    
    if (!isManager) {
      alert("팀장만 승인/반려가 가능합니다.")
      return
    }
    
    console.log("[REJECT] Starting rejection for report:", {
      reportId: reportToReject._id,
      userName: reportToReject.userName,
      weekStart: reportToReject.weekStart,
      weekEnd: reportToReject.weekEnd,
      currentApprovalStatus: reportToReject.managerApproval
    })
    
    try {
      const now = new Date().toISOString()
      
      console.log("[REJECT] Updating report with ID:", reportToReject._id)
      
      await lumi.entities.weeklyReports.update(reportToReject._id, {
        managerApproval: "rejected",
        isApproved: false,
        approvedBy: "",
        approvedAt: ""
      })

      console.log("[REJECT] Report updated successfully")
      
      // 반려 사유를 rejection_reasons 컬렉션에 저장 (upsert 방식)
      try {
        // 팀장의 실제 이름 조회
        let managerName = user?.userName || ""
        if (user?.userId) {
          try {
            const managerProfile = await lumi.entities.userProfiles3.list({
              filter: { userId: user.userId },
              limit: 1
            })
            if (managerProfile.list.length > 0) {
              managerName = managerProfile.list[0].userName || user.userName || ""
              console.log("[REJECT] Manager name from profile:", managerName)
            }
          } catch (profileError) {
            console.error("[REJECT] Failed to load manager profile:", profileError)
          }
        }
        
        // 기존 반려 사유 확인
        const existingRejection = await lumi.entities.rejection_reasons.list({
          filter: {
            reportId: reportToReject._id,
            weekStart: reportToReject.weekStart,
            weekEnd: reportToReject.weekEnd
          },
          limit: 1
        })
        
        const rejectionData = {
          reportId: reportToReject._id,
          userId: reportToReject.userId,
          userName: reportToReject.userName,
          managerId: user?.userId || "",
          managerName: managerName,
          weekStart: reportToReject.weekStart,
          weekEnd: reportToReject.weekEnd,
          reason: rejectReason.trim(),
          rejectedAt: now,
          updatedAt: now
        }
        
        if (existingRejection.list.length > 0) {
          // 기존 반려 사유 업데이트
          await lumi.entities.rejection_reasons.update(existingRejection.list[0]._id, rejectionData)
          console.log("[REJECT] Updated existing rejection reason:", existingRejection.list[0]._id)
        } else {
          // 새로운 반려 사유 생성
          await lumi.entities.rejection_reasons.create({
            ...rejectionData,
            creator: user?.userId || "",
            createdAt: now
          })
          console.log("[REJECT] Created new rejection reason")
        }
      } catch (rejectionError) {
        console.error("[REJECT] Failed to save rejection reason:", rejectionError)
      }

      // User에게 반려 알림 생성
      const weekStartDate = new Date(reportToReject.weekStart)
      const month = weekStartDate.getMonth() + 1
      const firstDay = new Date(weekStartDate.getFullYear(), weekStartDate.getMonth(), 1)
      const weekNumber = Math.ceil((weekStartDate.getDate() + firstDay.getDay()) / 7)

      const notificationData = {
        userId: reportToReject.userId,
        type: "rejected",
        message: `${month}월 ${weekNumber}주차 주간보고가 반려되었습니다. 수정 후 다시 제출해주세요.`,
        reportId: reportToReject._id,
        authorId: reportToReject.userId,
        authorName: reportToReject.userName,
        teamId: reportToReject.teamId,
        teamName: reportToReject.teamName,
        weekStart: reportToReject.weekStart,
        weekEnd: reportToReject.weekEnd,
        createdAt: now,
        isRead: false,
        isDeleted: false
      }
      
      console.log("[REJECTION] Creating notification with data:", JSON.stringify(notificationData, null, 2))
      
      const notification = await lumi.entities.notifications.create(notificationData)
      
      console.log("[REJECTION] Notification created successfully:", notification._id)
      console.log("[REJECTION] Full notification object:", JSON.stringify(notification, null, 2))
      
      // 생성된 알림이 실제로 DB에 있는지 확인
      const verifyResult = await lumi.entities.notifications.list({
        filter: { _id: notification._id },
        limit: 1
      })
      console.log("[REJECTION] Verification - notification in DB:", verifyResult.list.length > 0 ? "YES" : "NO")
      if (verifyResult.list.length > 0) {
        console.log("[REJECTION] Verified notification:", JSON.stringify(verifyResult.list[0], null, 2))
      }
      
      // 해당 주간의 투입시간도 함께 반려 (submitted -> draft)
      try {
        console.log("[REJECT] Finding time entries to revert to draft:", {
          userId: reportToReject.userId,
          weekStart: reportToReject.weekStart,
          status: "submitted"
        })
        
        const timeEntriesResult = await lumi.entities.time_entries2.list({
          filter: {
            userId: reportToReject.userId,
            weekStart: reportToReject.weekStart,
            status: "submitted"
          }
        })
        
        console.log("[REJECT] Found time entries:", timeEntriesResult.list.length)
        
        if (timeEntriesResult.list.length > 0) {
          for (const entry of timeEntriesResult.list) {
            await lumi.entities.time_entries2.update(entry._id, {
              status: "draft"
            })
            console.log("[REJECT] Reverted time entry to draft:", entry._id)
          }
          console.log("[REJECT] All time entries reverted to draft successfully")
        } else {
          console.log("[REJECT] No submitted time entries found for this week")
        }
      } catch (timeEntryError) {
        console.error("[REJECT] Failed to revert time entries to draft:", timeEntryError)
      }
      
      const weekEndDate = new Date(reportToReject.weekEnd)
      alert(`✅ 반려가 완료되었습니다.\n\n[반려된 보고서]\n• 작성자: ${reportToReject.userName}\n• 보고 기간: ${formatDate(weekStartDate)} ~ ${formatDate(weekEndDate)}\n\n팀원에게 다시 제출하도록 안내해주세요.`)
      
      closeRejectModal()
      await loadWeeklyReports()
      
      // 선택 해제
      if (selectedReport?._id === reportToReject._id) {
        setSelectedReport(null)
      }
      
      console.log("[REJECT] Rejection process completed successfully")
    } catch (error) {
      console.error("[REJECT] Failed to reject report:", error)
      alert("❌ 반려 처리 중 오류가 발생했습니다.")
    }
  }
  
  // CEO 승인 함수
  const handleCeoApprove = async (report: any) => {
    if (!isCLevel) {
      alert("임원만 최종 승인이 가능합니다.")
      return
    }
    
    if (!window.confirm(`${report.userName}님의 주간보고를 최종 승인하시겠습니까?`)) {
      return
    }
    
    try {
      const now = new Date().toISOString()
      
      await lumi.entities.weeklyReports.update(report._id, {
        ceoApproval: "approved",
        ceoApprovedAt: now,
        ceoApproverId: user?.userId,
        ceoApproverName: user?.userName || ""
      })
      
      // CEO 승인 시 rejection_reasons2 데이터 삭제
      try {
        const rejectionResult = await lumi.entities.rejection_reasons2.list({
          filter: {
            reportId: report._id
          }
        })
        
        if (rejectionResult.list.length > 0) {
          for (const rejection of rejectionResult.list) {
            await lumi.entities.rejection_reasons2.delete(rejection._id)
            console.log("[CEO APPROVAL] Deleted rejection reason:", rejection._id)
          }
        }
      } catch (deleteError) {
        console.error("[CEO APPROVAL] Failed to delete rejection reasons:", deleteError)
      }
      
      // User에게 CEO 승인 알림 생성
      const weekStartDate = new Date(report.weekStart)
      const month = weekStartDate.getMonth() + 1
      const firstDay = new Date(weekStartDate.getFullYear(), weekStartDate.getMonth(), 1)
      const weekNumber = Math.ceil((weekStartDate.getDate() + firstDay.getDay()) / 7)
      
      const notificationData = {
        userId: report.userId,
        type: "ceo_approved",
        message: `${month}월 ${weekNumber}주차 주간보고가 CEO 최종 승인되었습니다.`,
        reportId: report._id,
        authorId: report.userId,
        authorName: report.userName,
        teamId: report.teamId,
        teamName: report.teamName,
        weekStart: report.weekStart,
        weekEnd: report.weekEnd,
        createdAt: now,
        isRead: false,
        isDeleted: false
      }
      
      console.log("[CEO APPROVAL] Creating notification with data:", JSON.stringify(notificationData, null, 2))
      
      const notification = await lumi.entities.notifications.create(notificationData)
      
      console.log("[CEO APPROVAL] Notification created successfully:", notification._id)
      
      alert("✅ CEO 최종 승인이 완료되었습니다.")
      await loadWeeklyReports()
      
      // 선택된 보고서 갱신
      if (selectedReport?._id === report._id) {
        const updated = { 
          ...report, 
          ceoApproval: "approved",
          ceoApprovedAt: now,
          ceoApproverId: user?.userId,
          ceoApproverName: user?.userName || ""
        }
        setSelectedReport(updated)
      }
    } catch (error) {
      console.error("[CEO APPROVAL] Failed to approve report:", error)
      alert("❌ CEO 승인 처리 중 오류가 발생했습니다.")
    }
  }
  
  // CEO 반려 모달 열기
  const openCeoRejectModal = (report: any) => {
    setReportToCeoReject(report)
    setCeoRejectReason("")
    setShowCeoRejectModal(true)
  }
  
  // CEO 반려 모달 닫기
  const closeCeoRejectModal = () => {
    setShowCeoRejectModal(false)
    setReportToCeoReject(null)
    setCeoRejectReason("")
  }
  
  // CEO 반려 함수
  const handleCeoReject = async () => {
    if (!reportToCeoReject) return
    
    if (!ceoRejectReason.trim() || ceoRejectReason.trim().length < 10) {
      alert("반려 사유를 10자 이상 입력해주세요.")
      return
    }
    
    if (!isCLevel) {
      alert("임원만 최종 반려가 가능합니다.")
      return
    }
    
    console.log("[CEO REJECT] Starting CEO rejection for report:", {
      reportId: reportToCeoReject._id,
      userName: reportToCeoReject.userName,
      weekStart: reportToCeoReject.weekStart,
      weekEnd: reportToCeoReject.weekEnd
    })
    
    try {
      const now = new Date().toISOString()
      
      console.log("[CEO REJECT] Updating report with ID:", reportToCeoReject._id)
      
      await lumi.entities.weeklyReports.update(reportToCeoReject._id, {
        ceoApproval: "rejected",
        ceoApprovedAt: now,
        ceoApproverId: user?.userId,
        ceoApproverName: user?.userName || ""
      })
      
      console.log("[CEO REJECT] Report updated successfully")
      
      // CEO 반려 사유를 rejection_reasons2 컬렉션에 저장 (upsert 방식)
      try {
        // CEO의 실제 이름 조회
        let ceoName = user?.userName || ""
        if (user?.userId) {
          try {
            const ceoProfile = await lumi.entities.userProfiles3.list({
              filter: { userId: user.userId },
              limit: 1
            })
            if (ceoProfile.list.length > 0) {
              ceoName = ceoProfile.list[0].userName || user.userName || ""
              console.log("[CEO REJECT] CEO name from profile:", ceoName)
            }
          } catch (profileError) {
            console.error("[CEO REJECT] Failed to load CEO profile:", profileError)
          }
        }
        
        // 기존 CEO 반려 사유 확인
        const existingRejection = await lumi.entities.rejection_reasons2.list({
          filter: {
            reportId: reportToCeoReject._id
          },
          limit: 1
        })
        
        const rejectionData = {
          reportId: reportToCeoReject._id,
          userId: user?.userId || "",
          userName: ceoName,
          reason: ceoRejectReason.trim(),
          createdAt: now
        }
        
        if (existingRejection.list.length > 0) {
          // 기존 반려 사유 업데이트
          await lumi.entities.rejection_reasons2.update(existingRejection.list[0]._id, rejectionData)
          console.log("[CEO REJECT] Updated existing rejection reason:", existingRejection.list[0]._id)
        } else {
          // 새로운 반려 사유 생성
          await lumi.entities.rejection_reasons2.create(rejectionData)
          console.log("[CEO REJECT] Created new rejection reason")
        }
      } catch (rejectionError) {
        console.error("[CEO REJECT] Failed to save rejection reason:", rejectionError)
      }
      
      // User에게 CEO 반려 알림 생성
      const weekStartDate = new Date(reportToCeoReject.weekStart)
      const month = weekStartDate.getMonth() + 1
      const firstDay = new Date(weekStartDate.getFullYear(), weekStartDate.getMonth(), 1)
      const weekNumber = Math.ceil((weekStartDate.getDate() + firstDay.getDay()) / 7)
      
      const notificationData = {
        userId: reportToCeoReject.userId,
        type: "ceo_rejected",
        message: `${month}월 ${weekNumber}주차 주간보고가 CEO에 의해 반려되었습니다. 수정 후 다시 제출해주세요.`,
        reportId: reportToCeoReject._id,
        authorId: reportToCeoReject.userId,
        authorName: reportToCeoReject.userName,
        teamId: reportToCeoReject.teamId,
        teamName: reportToCeoReject.teamName,
        weekStart: reportToCeoReject.weekStart,
        weekEnd: reportToCeoReject.weekEnd,
        createdAt: now,
        isRead: false,
        isDeleted: false
      }
      
      console.log("[CEO REJECTION] Creating notification with data:", JSON.stringify(notificationData, null, 2))
      
      const notification = await lumi.entities.notifications.create(notificationData)
      
      console.log("[CEO REJECTION] Notification created successfully:", notification._id)
      
      // 해당 주간의 투입시간도 함께 반려 (submitted -> draft)
      try {
        console.log("[CEO REJECT] Finding time entries to revert to draft:", {
          userId: reportToCeoReject.userId,
          weekStart: reportToCeoReject.weekStart,
          status: "submitted"
        })
        
        const timeEntriesResult = await lumi.entities.time_entries2.list({
          filter: {
            userId: reportToCeoReject.userId,
            weekStart: reportToCeoReject.weekStart,
            status: "submitted"
          }
        })
        
        console.log("[CEO REJECT] Found time entries:", timeEntriesResult.list.length)
        
        if (timeEntriesResult.list.length > 0) {
          for (const entry of timeEntriesResult.list) {
            await lumi.entities.time_entries2.update(entry._id, {
              status: "draft"
            })
            console.log("[CEO REJECT] Reverted time entry to draft:", entry._id)
          }
          console.log("[CEO REJECT] All time entries reverted to draft successfully")
        } else {
          console.log("[CEO REJECT] No submitted time entries found for this week")
        }
      } catch (timeEntryError) {
        console.error("[CEO REJECT] Failed to revert time entries to draft:", timeEntryError)
      }
      
      const weekEndDate = new Date(reportToCeoReject.weekEnd)
      alert(`✅ CEO 반려가 완료되었습니다.\n\n[반려된 보고서]\n• 작성자: ${reportToCeoReject.userName}\n• 보고 기간: ${formatDate(weekStartDate)} ~ ${formatDate(weekEndDate)}\n\n팀원에게 다시 제출하도록 안내해주세요.`)
      
      closeCeoRejectModal()
      await loadWeeklyReports()
      
      // 선택 해제
      if (selectedReport?._id === reportToCeoReject._id) {
        setSelectedReport(null)
      }
      
      console.log("[CEO REJECT] CEO rejection process completed successfully")
    } catch (error) {
      console.error("[CEO REJECT] Failed to reject report:", error)
      alert("❌ CEO 반려 처리 중 오류가 발생했습니다.")
    }
  }

  const handleNavigateToIssue = (issue: LinkedIssue) => {
    setPendingIssueTarget({ equipmentId: issue.equipmentId, issueId: issue.issueId })
    window.dispatchEvent(new Event("navigateToEquipmentIssue"))
  }

  const getProjectTimeSummary = () => {
    const summary: { [projectName: string]: { [activityCode: string]: number } } = {}

    timeEntries.forEach((entry: any) => {
      if (entry.isVacation) return

      if (!entry.projectName || 
          entry.projectName === "연차" || 
          entry.projectName === "오전반차" || 
          entry.projectName === "오후반차") {
        return
      }

      if (!summary[entry.projectName]) {
        summary[entry.projectName] = {}
      }

      if (entry.dailyEntries) {
        entry.dailyEntries.forEach((daily: any) => {
          if (daily.activities) {
            daily.activities.forEach((activity: any) => {
              const code = activity.activityCode
              const hours = activity.hours || 0
              
              if (!code || hours === 0) return
              
              if (!summary[entry.projectName][code]) {
                summary[entry.projectName][code] = 0
              }
              summary[entry.projectName][code] += hours
            })
          }
        })
      }
    })

    return summary
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl shadow-lg">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                주간보고 확인
              </h1>
              <p className="text-gray-600 mt-1">직원들의 주간보고를 확인하세요</p>
            </div>
          </div>
        </div>

        {/* Week Navigation */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <button
              onClick={previousWeek}
              className="p-2 hover:bg-orange-50 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-orange-600" />
            </button>

            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-orange-600" />
              <span className="text-lg font-semibold text-gray-900">
                {formatDate(selectedWeek)} ~ {formatDate(getWeekEnd(selectedWeek))}
              </span>
            </div>

            <button
              onClick={nextWeek}
              className="p-2 hover:bg-orange-50 rounded-lg transition-colors"
            >
              <ChevronRight className="w-6 h-6 text-orange-600" />
            </button>
          </div>
        </div>

        {/* C-Level Team Filter */}
        {isCLevel && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-orange-600" />
                <span className="font-semibold text-gray-900">팀 필터:</span>
              </div>
              <select
                value={selectedTeamFilter}
                onChange={(e) => setSelectedTeamFilter(e.target.value)}
                className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors bg-white text-gray-900 font-medium hover:border-orange-300 cursor-pointer"
              >
                <option value="all">전체 팀</option>
                {teams.map((team) => (
                  <option key={team.teamId} value={team.teamId}>
                    {team.teamName}
                  </option>
                ))}
              </select>
              {selectedTeamFilter !== "all" && (
                <button
                  onClick={() => setSelectedTeamFilter("all")}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  필터 초기화
                </button>
              )}
            </div>
            {selectedTeamFilter !== "all" && (
              <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800">
                  <span className="font-semibold">
                    {teams.find(t => t.teamId === selectedTeamFilter)?.teamName}
                  </span> 팀의 주간보고만 표시됩니다.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Reports List - 전체 폭, 1줄 2~4명 카드 그리드 (최대 8개=2줄, 이후 스크롤) */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div>
            <div>
              {/* Tab Navigation */}
              {isManager && (
                <div className="flex gap-2 mb-4 border-b">
                  <button
                    onClick={() => setShowRejected(false)}
                    className={`flex-1 py-2 px-4 text-sm font-semibold transition-colors ${
                      !showRejected
                        ? "border-b-2 border-orange-600 text-orange-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    제출된 보고서 ({reports.length})
                  </button>
                  <button
                    onClick={() => setShowRejected(true)}
                    className={`flex-1 py-2 px-4 text-sm font-semibold transition-colors ${
                      showRejected
                        ? "border-b-2 border-red-600 text-red-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    반려 보고서 ({rejectedReports.length})
                  </button>
                </div>
              )}
              
              {!isManager && (
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-orange-600" />
                  제출된 보고서 ({reports.length})
                </h2>
              )}

              {loading ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                  <p className="mt-2">로딩 중...</p>
                </div>
              ) : (showRejected ? rejectedReports : reports).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p>{showRejected ? "반려된 보고서가 없습니다" : "제출된 보고서가 없습니다"}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 max-h-[300px] sm:max-h-[260px] overflow-y-auto pr-1">
                  {(showRejected ? rejectedReports : reports).map((report) => (
                    <div
                      key={report._id}
                      onClick={() => setSelectedReport(report)}
                      className={`p-2.5 sm:p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md ${
                        selectedReport?._id === report._id
                          ? showRejected 
                            ? "border-red-500 bg-red-50"
                            : "border-orange-500 bg-orange-50"
                          : "border-gray-200 hover:border-orange-300"
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 mb-1.5 sm:mb-2">
                        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                          <User className="w-4 h-4 text-orange-600 flex-shrink-0" />
                          <span className="font-semibold text-gray-900 text-sm sm:text-base truncate">{report.userName}</span>
                        </div>
                        {showRejected ? (
                          <span className="self-start text-[10px] sm:text-xs bg-red-100 text-red-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full whitespace-nowrap">
                            <span className="sm:hidden">반려</span>
                            <span className="hidden sm:inline">반려됨</span>
                          </span>
                        ) : report.ceoApproval === "approved" ? (
                          <span className="self-start text-[10px] sm:text-xs bg-blue-100 text-blue-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full whitespace-nowrap">
                            <span className="sm:hidden">최종승인</span>
                            <span className="hidden sm:inline">대표이사 승인 완료</span>
                          </span>
                        ) : report.isApproved ? (
                          <span className="self-start text-[10px] sm:text-xs bg-green-100 text-green-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full whitespace-nowrap">
                            <span className="sm:hidden">팀장승인</span>
                            <span className="hidden sm:inline">팀장 승인 완료</span>
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600 space-y-0.5 sm:space-y-1">
                        <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                          <span className="text-gray-500 hidden sm:inline">소속:</span>
                          <span className="truncate">{report.teamName}</span>
                        </div>
                        <div className="hidden sm:flex items-center gap-2">
                          <span className="text-gray-500">제목:</span>
                          <span className="font-medium text-gray-900">주간보고</span>
                        </div>
                        <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                          <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="text-[11px] sm:text-xs text-gray-500 truncate">
                            <span className="sm:hidden">{formatDateShort(new Date(report.submittedAt))}</span>
                            <span className="hidden sm:inline">{formatDateTime(new Date(report.submittedAt))}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Report Detail - 전체 폭 */}
        <div>
          {selectedReport ? (
              <div className="bg-white rounded-xl shadow-lg p-8">
                {/* Report Header */}
                <div className="border-b-2 border-gray-200 pb-6 mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">주간 업무 보고</h2>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="font-semibold">작성자:</span> {selectedReport.userName}</div>
                    <div><span className="font-semibold">소속:</span> {selectedReport.teamName}</div>
                    <div><span className="font-semibold">보고 기간:</span> {formatDate(new Date(selectedReport.weekStart))} ~ {formatDate(new Date(selectedReport.weekEnd))}</div>
                    <div><span className="font-semibold">제출일:</span> {formatDateTimeWithSeconds(new Date(selectedReport.submittedAt))}</div>
                  </div>
                  {selectedReport.ceoApproval === "approved" ? (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        ✓ 대표이사 승인이 완료되었습니다 (승인일: {formatDateTimeWithSeconds(new Date(selectedReport.ceoApprovedAt))})
                      </p>
                    </div>
                  ) : selectedReport.isApproved ? (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800">
                        ✓ 팀장 승인이 완료되었습니다 (승인일: {formatDateTimeWithSeconds(new Date(selectedReport.approvedAt))})
                      </p>
                    </div>
                  ) : null}
                  {selectedReport.managerApproval === "rejected" && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800">
                        ✗ 반려됨 - 재제출 필요
                      </p>
                    </div>
                  )}
                  
                  {/* Manager 승인/반려 버튼 */}
                  {isManager && selectedReport.managerApproval !== "rejected" && !selectedReport.isApproved && (
                    <div className="mt-4 flex gap-3">
                      <button
                        onClick={() => handleApprove(selectedReport)}
                        className="flex-1 py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 font-semibold"
                      >
                        <CheckCircle className="w-5 h-5" />
                        승인
                      </button>
                      <button
                        onClick={() => openRejectModal(selectedReport)}
                        className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 font-semibold"
                      >
                        <XCircle className="w-5 h-5" />
                        반려
                      </button>
                    </div>
                  )}
                  
                  {/* CEO 승인/반려 버튼 */}
                  {isCLevel && selectedReport.managerApproval === "approved" && selectedReport.ceoApproval !== "approved" && selectedReport.ceoApproval !== "rejected" && (
                    <div className="mt-4 flex gap-3">
                      <button
                        onClick={() => handleCeoApprove(selectedReport)}
                        className="flex-1 py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 font-semibold"
                      >
                        <CheckCircle className="w-5 h-5" />
                        최종 승인
                      </button>
                      <button
                        onClick={() => openCeoRejectModal(selectedReport)}
                        className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 font-semibold"
                      >
                        <XCircle className="w-5 h-5" />
                        반려
                      </button>
                    </div>
                  )}
                </div>

                {/* 금주 실적 비교: 좌=이전주 금주실적, 우=현재 금주실적 (레거시 content fallback 포함) */}
                <div className="mb-8 print:hidden">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* 좌측: 이전주 금주 실적 */}
                    <div className="rounded-xl border-2 border-gray-200 bg-gray-50/60 p-5">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-bold text-gray-700 flex items-center gap-2">
                          <span className="inline-block w-1.5 h-5 bg-gray-400 rounded-full" />
                          이전주 실적
                        </h3>
                        {prevWeekReport && (
                          <span className="text-xs text-gray-500">
                            {formatDate(new Date(prevWeekReport.weekStart))} ~ {formatDate(new Date(prevWeekReport.weekEnd))}
                          </span>
                        )}
                      </div>
                      {prevWeekReport ? (
                        (prevWeekReport.thisWeekContent || prevWeekReport.content) ? (
                          <div
                            className="prose ql-editor max-w-none text-gray-700"
                            dangerouslySetInnerHTML={{ __html: prevWeekReport.thisWeekContent || prevWeekReport.content }}
                          />
                        ) : (
                          <p className="text-sm text-gray-400">이전주 실적 내용이 없습니다.</p>
                        )
                      ) : (
                        <p className="text-sm text-gray-400">이전주에 제출된 보고서가 없습니다.</p>
                      )}
                    </div>

                    {/* 우측: 현재(선택주) 금주 실적 */}
                    <div className="rounded-xl border-2 border-orange-200 bg-orange-50/40 p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="inline-block w-1.5 h-5 bg-orange-500 rounded-full" />
                        금주 실적
                      </h3>
                      {(selectedReport.thisWeekContent || selectedReport.nextWeekPlan) ? (
                        selectedReport.thisWeekContent ? (
                          <div
                            className="prose ql-editor max-w-none text-gray-700"
                            dangerouslySetInnerHTML={{ __html: selectedReport.thisWeekContent }}
                          />
                        ) : (
                          <p className="text-sm text-gray-400">작성된 금주 실적이 없습니다.</p>
                        )
                      ) : (
                        <div
                          className="prose ql-editor max-w-none text-gray-700"
                          dangerouslySetInnerHTML={{ __html: selectedReport.content }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* 차주 계획(좌) / 장비 이슈(우) */}
                <div className="mb-8 print:hidden">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* 좌측: 차주 계획 */}
                    <div className="rounded-xl border-2 border-blue-200 bg-blue-50/40 p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full" />
                        차주 계획
                      </h3>
                      {selectedReport.nextWeekPlan ? (
                        <div
                          className="prose ql-editor max-w-none text-gray-700"
                          dangerouslySetInnerHTML={{ __html: selectedReport.nextWeekPlan }}
                        />
                      ) : (
                        <p className="text-sm text-gray-400">작성된 차주 계획이 없습니다.</p>
                      )}
                    </div>

                    {/* 우측: 관련 장비 이슈 */}
                    <div className="rounded-xl border-2 border-gray-200 bg-white p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <Link2 className="w-5 h-5 text-orange-600" />
                        관련 장비 이슈
                        {Array.isArray(selectedReport.linkedIssues) && selectedReport.linkedIssues.length > 0 && (
                          <span className="text-sm font-normal text-gray-500">({selectedReport.linkedIssues.length})</span>
                        )}
                      </h3>
                      {Array.isArray(selectedReport.linkedIssues) && selectedReport.linkedIssues.length > 0 ? (
                        <LinkedIssueBadges
                          issues={selectedReport.linkedIssues as LinkedIssue[]}
                          onNavigate={handleNavigateToIssue}
                        />
                      ) : (
                        <p className="text-sm text-gray-400">연결된 장비 이슈가 없습니다.</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Print-only Report Content - 인쇄 전용 2단 구조 (금주 실적/차주 계획, 레거시 content fallback 포함) */}
                <div className="hidden print:block mb-8 space-y-6">
                  {(selectedReport.thisWeekContent || selectedReport.nextWeekPlan) ? (
                    <>
                      <div
                        className="p-5 border border-gray-300 rounded-lg"
                        style={{ backgroundColor: "#f9fafb", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}
                      >
                        <h3 className="text-xl font-bold text-gray-900 mb-2">금주 실적</h3>
                        {selectedReport.thisWeekContent ? (
                          <div
                            className="prose ql-editor max-w-none text-gray-700"
                            dangerouslySetInnerHTML={{ __html: selectedReport.thisWeekContent }}
                          />
                        ) : (
                          <p className="text-sm text-gray-400">작성된 금주 실적이 없습니다.</p>
                        )}
                      </div>
                      <div
                        className="p-5 border border-blue-300 rounded-lg"
                        style={{ backgroundColor: "#eff6ff", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}
                      >
                        <h3 className="text-xl font-bold text-gray-900 mb-2">차주 계획</h3>
                        {selectedReport.nextWeekPlan ? (
                          <div
                            className="prose ql-editor max-w-none text-gray-700"
                            dangerouslySetInnerHTML={{ __html: selectedReport.nextWeekPlan }}
                          />
                        ) : (
                          <p className="text-sm text-gray-400">작성된 차주 계획이 없습니다.</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">보고 내용</h3>
                      <div
                        className="prose ql-editor max-w-none text-gray-700"
                        dangerouslySetInnerHTML={{ __html: selectedReport.content }}
                      />
                    </div>
                  )}
                </div>

                {/* Attachments Section */}
                {attachments.length > 0 && (
                  <div className="mb-8 pt-8 border-t-2 border-gray-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Paperclip className="w-6 h-6 text-orange-600" />
                      첨부파일 ({attachments.length})
                    </h3>
                    <div className="space-y-2">
                      {attachments.map((attachment: any) => (
                        <div
                          key={attachment._id}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-orange-300 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <Paperclip className="w-5 h-5 text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {attachment.fileName}
                              </p>
                              <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                                <span>{formatFileSize(attachment.fileSize)}</span>
                                <span>•</span>
                                <span>{new Date(attachment.uploadedAt).toLocaleDateString('ko-KR')}</span>
                                <span>•</span>
                                <span>{attachment.uploaderName}</span>
                              </div>
                            </div>
                          </div>
                          <a
                            href={attachment.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-3 p-2 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-colors flex-shrink-0"
                            title="다운로드"
                          >
                            <Download className="w-5 h-5" />
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Time Entries Summary */}
                <div className="mt-8 pt-8 border-t-2 border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Clock className="w-6 h-6 text-orange-600" />
                    주간 프로젝트 투입시간
                  </h3>

                  {(() => {
                    const projectTimeSummary = getProjectTimeSummary()
                    const hasTimeData = Object.keys(projectTimeSummary).length > 0

                    if (!hasTimeData) {
                      return (
                        <div className="text-center py-8 text-gray-500">
                          <p>해당 주간에 입력된 투입시간이 없습니다.</p>
                        </div>
                      )
                    }

                    return (
                      <>
                        {/* Activity Legend */}
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">활동코드 범례</h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {Object.entries(ACTIVITY_COLORS).map(([code, color]) => (
                              <div key={code} className="flex items-center gap-2">
                                <div 
                                  className="w-4 h-4 rounded"
                                  style={{ backgroundColor: color }}
                                />
                                <span className="text-sm text-gray-700">{code}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Project Time Table */}
                        <div className="space-y-4">
                          {Object.entries(projectTimeSummary).map(([projectName, activities]) => {
                            const totalHours = Object.values(activities).reduce((sum: number, hours: any) => sum + hours, 0)
                            return (
                              <div key={projectName} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="font-semibold text-gray-900">{projectName}</h4>
                                  <span className="text-sm font-medium text-orange-600">
                                    총 {totalHours.toFixed(1)}시간
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                  {Object.entries(activities).map(([activityCode, hours]: [string, any]) => (
                                    <div 
                                      key={activityCode}
                                      className="flex items-center justify-between px-3 py-2 rounded-lg"
                                      style={{ 
                                        backgroundColor: `${ACTIVITY_COLORS[activityCode as keyof typeof ACTIVITY_COLORS]}40`,
                                        borderLeft: `4px solid ${ACTIVITY_COLORS[activityCode as keyof typeof ACTIVITY_COLORS]}`
                                      }}
                                    >
                                      <span className="text-sm text-gray-700">{activityCode}</span>
                                      <span className="text-sm font-semibold text-gray-900">{hours.toFixed(1)}h</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </>
                    )
                  })()}
                </div>
              </div>
          ) : (
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center py-16 text-gray-500">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg">상단 목록에서 보고서를 선택하세요</p>
              </div>
            </div>
          )}
        </div>

        {/* 반려 사유 입력 모달 */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">주간보고 반려</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {reportToReject?.userName}님의 주간보고를 반려합니다
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeRejectModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XCircle className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  반려 사유 <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="반려 사유를 상세히 입력해주세요. (최소 10자 이상)"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none transition-colors resize-none"
                  rows={6}
                  autoFocus
                />
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    최소 10자 이상 입력해주세요
                  </span>
                  <span className={`font-medium ${rejectReason.length < 10 ? 'text-red-600' : 'text-green-600'}`}>
                    {rejectReason.length}자
                  </span>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-semibold mb-1">안내사항</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>반려 사유는 작성자에게 전달됩니다</li>
                      <li>작성자는 내용을 수정하여 다시 제출할 수 있습니다</li>
                      <li>명확하고 구체적인 사유를 입력해주세요</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={closeRejectModal}
                  className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-semibold"
                >
                  취소
                </button>
                <button
                  onClick={handleReject}
                  disabled={!rejectReason.trim() || rejectReason.trim().length < 10}
                  className="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <XCircle className="w-5 h-5" />
                  반려 확정
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* CEO 반려 사유 입력 모달 */}
        {showCeoRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">주간보고 반려</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {reportToCeoReject?.userName}님의 주간보고를 반려합니다
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeCeoRejectModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XCircle className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  반려 사유 <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={ceoRejectReason}
                  onChange={(e) => setCeoRejectReason(e.target.value)}
                  placeholder="반려 사유를 상세히 입력해주세요. (최소 10자 이상)"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none transition-colors resize-none"
                  rows={6}
                  autoFocus
                />
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    최소 10자 이상 입력해주세요
                  </span>
                  <span className={`font-medium ${ceoRejectReason.length < 10 ? 'text-red-600' : 'text-green-600'}`}>
                    {ceoRejectReason.length}자
                  </span>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-semibold mb-1">안내사항</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>반려 사유는 작성자에게 전달됩니다</li>
                      <li>작성자는 내용을 수정하여 다시 제출할 수 있습니다</li>
                      <li>명확하고 구체적인 사유를 입력해주세요</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={closeCeoRejectModal}
                  className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-semibold"
                >
                  취소
                </button>
                <button
                  onClick={handleCeoReject}
                  disabled={!ceoRejectReason.trim() || ceoRejectReason.trim().length < 10}
                  className="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <XCircle className="w-5 h-5" />
                  반려 확정
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function getMonday(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  return new Date(d.setDate(diff))
}

function getWeekEnd(startDate: Date): Date {
  const end = new Date(startDate)
  end.setDate(end.getDate() + 6)
  return end
}

function formatDate(date: Date): string {
  return date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).replace(/\. /g, ".").replace(".", "년 ").replace(".", "월 ").replace(".", "일")
}

function formatDateTime(date: Date): string {
  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).replace(/\. /g, ".").replace(".", "년 ").replace(".", "월 ").replace(".", "일 ")
}

// 모바일 2열 카드용 축약 날짜 (예: 09.03)
function formatDateShort(date: Date): string {
  return date
    .toLocaleDateString("ko-KR", { month: "2-digit", day: "2-digit" })
    .replace(/\s/g, "")
    .replace(/\.$/, "")
}

function formatDateTimeWithSeconds(date: Date): string {
  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).replace(/\. /g, ".").replace(".", "년 ").replace(".", "월 ").replace(".", "일 ")
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}
